/**
 * MTL — Personalizer preview + finish default (2026-08-11)
 *
 * History: this file began (2026-07-05) as the "neutral preview" gate — no swatch
 * pre-selected and a "Pick a finish to preview" placeholder instead of the gold
 * pendant. On 2026-07-28b the placeholder was retired storewide (owner) and the
 * gold base image now shows on load. What survived that retirement was
 * clearDefaultSelection(): it kept stripping the server-rendered active gold
 * swatch even though the reason for stripping it was gone. So the preview showed
 * gold while the swatches showed nothing chosen — the UI contradicting itself.
 *
 * This version drops the clearing. The server marks the gold swatch active
 * (variant.id == product.selected_or_first_available_variant.id) and that is now
 * left alone; if for any reason nothing is active, gold is marked active.
 *
 * NOT changed by this file: the pendant preview / image / engraving logic (that
 * lives in mtl-personalization.js), the "Your Preview" thumbnail fill below, and
 * the single-finish guard for hide_finish_step products.
 *
 * Reversible: restore from the dated snapshot in Ops/snapshots/.
 */
(function () {
  'use strict';

  /**
   * Keep the server's default finish selection (gold) instead of clearing it.
   * Never fights a real customer choice.
   */
  function ensureDefaultSelection(form) {
    if (form.classList.contains('mtl-finish-chosen')) return;
    if (form.querySelector('[data-mtl-swatch].active')) return;

    // Mirror the variant the server already bound - never invent a choice.
    var idInput = form.querySelector('input[name="id"]');
    var bound = idInput && form.querySelector(
      '[data-mtl-swatch][data-variant-id="' + idInput.value + '"]'
    );
    if (bound) {
      bound.classList.add('active');
      bound.setAttribute('aria-pressed', 'true');
      return;
    }
    // Variant-bound swatches present but none matches: leave alone (two-tone).
    if (form.querySelector('[data-mtl-swatch][data-variant-id]')) return;

    var gold = form.querySelector('[data-mtl-swatch="gold"]');
    if (gold) {
      gold.classList.add('active');
      gold.setAttribute('aria-pressed', 'true');
    }
  }

  /**
   * "Your Preview" thumbnail image fill (storewide, 2026-07-05). Unchanged.
   * The first .mockup-thumb is a text-only "Your Preview" tile that reads as empty.
   * Inject a small gold-base <img> UNDER the existing label so the tile shows a real
   * pendant image (label + LIVE indicator stay visible on top). Idempotent.
   */
  function fillPreviewThumb(form) {
    var firstThumb = form.querySelector('.mockup-thumb');
    if (!firstThumb) return;
    if (firstThumb.querySelector('img')) return;
    var goldSrc = form.getAttribute('data-img-gold');
    if (!goldSrc) return;
    if (getComputedStyle(firstThumb).position === 'static') {
      firstThumb.style.position = 'relative';
    }
    firstThumb.style.overflow = 'hidden';
    var img = document.createElement('img');
    img.setAttribute('data-mtl', 'preview-thumb-img');
    img.setAttribute('alt', '');
    img.setAttribute('loading', 'lazy');
    img.src = goldSrc;
    img.style.cssText = [
      'position:absolute', 'inset:0', 'width:100%', 'height:100%',
      'object-fit:cover', 'z-index:0', 'pointer-events:none'
    ].join(';');
    firstThumb.insertBefore(img, firstThumb.firstChild);
    var scrim = document.createElement('span');
    scrim.setAttribute('data-mtl', 'preview-thumb-scrim');
    scrim.style.cssText = [
      'position:absolute', 'inset:0', 'z-index:0', 'pointer-events:none',
      'background:linear-gradient(180deg, rgba(255,255,255,.35) 0%, rgba(255,255,255,.62) 100%)'
    ].join(';');
    firstThumb.insertBefore(scrim, img.nextSibling);
    var lbl = firstThumb.querySelector('.thumb-label');
    if (lbl) {
      lbl.style.position = 'relative';
      lbl.style.zIndex = '1';
    }
  }

  function initForm(form) {
    if (!form || form.dataset.mtlNeutralInit === 'true') return;
    var wrap = form.querySelector('.pendant-img-wrap');
    var inner = form.querySelector('.pendant-inner');
    if (!wrap || !inner) return;
    form.dataset.mtlNeutralInit = 'true';

    fillPreviewThumb(form);

    // SINGLE-FINISH GUARD (2026-07-05b): products that render no finish swatches
    // (hide_finish_step, e.g. the sliding-bar hidden-message necklaces) have
    // nothing to pick. Unchanged.
    if (form.querySelectorAll('[data-mtl-swatch]').length === 0) {
      form.classList.add('mtl-finish-chosen');
      return;
    }

    // GOLD PRESELECT (2026-08-11): keep the server's gold selection. The gold
    // base image is already the initial pendant-img src, so preview and swatch
    // now agree on load. Re-assert briefly in case base JS runs async.
    ensureDefaultSelection(form);
    setTimeout(function () { ensureDefaultSelection(form); }, 120);
    setTimeout(function () { ensureDefaultSelection(form); }, 400);
  }

  function init() {
    document.querySelectorAll('form.mtl-p').forEach(initForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
